import React from 'react';
import {
	Building2,
	Calculator,
	FileText,
	Gift,
	Home,
	PiggyBank,
	Receipt,
	Settings,
	Users
} from 'lucide-react';
import type { NavItemData } from '../components/arca-nav/sidebarNavItems';
import type { TextosDeNavegacion } from '../components/arca-nav/navTextos';

/**
 * Lo que los bloques de navegación de Proa (`components/arca-nav`, Arca-Team/product#55) necesitan
 * del Dashboard y no son datos de planes (eso es `dashboardNavigation.ts`): los textos traducidos y
 * las secciones, a partir de las pestañas de la vista.
 */

/** Una pestaña del Dashboard, solo con lo que se lee aquí. */
export interface PestanaDeNavegacion {
	label: string;
	route: string;
}

/**
 * Los textos de Proa, traducidos. Los dos de administración llevan su hueco (`{empresa}`, `{n}`), que
 * rellena Proa. Ver `components/arca-nav/navTextos.ts`.
 */
export const textosDeNavegacion = (
	translate: (key: string) => string,
	{ sinNombreDeEmpresa = false }: { sinNombreDeEmpresa?: boolean } = {}
): TextosDeNavegacion => ({
	seleccionarPlan: translate('general.nav.selectPlan'),
	selectorDePlan: translate('general.nav.planSelector'),
	resumenDePlanes: translate('general.avatar.resumen'),
	anadirPlan: translate('general.nav.addPlan'),
	enProceso: translate('general.avatar.pendingPersonalPlanStatus'),
	seleccionarEmpresa: translate('general.nav.selectCompany'),
	selectorDeEmpresa: translate('general.nav.companySelector'),
	admin: translate('general.avatar.adminContext'),
	personal: translate('general.avatar.personalContext'),
	// Sin el nombre de la empresa, «Admin» a secas: «Admin {empresa}» saldría «Admin ».
	adminDeUnaEmpresa: translate(
		sinNombreDeEmpresa ? 'general.avatar.adminContext' : 'general.nav.adminOfCompany'
	),
	adminDeVariasEmpresas: translate('general.nav.adminOfCompanies'),
	menuDeUsuario: translate('general.nav.userMenu'),
	configuracion: translate('user.dashboard.userSettings'),
	soporte: translate('user.settings.support'),
	cerrarSesion: translate('general.button.logOut'),
	colapsarMenu: translate('general.menu.collapse'),
	expandirMenu: translate('general.menu.expand'),
	tusPlanes: translate('general.nav.yourPlans'),
	tusEmpresas: translate('general.nav.yourCompanies'),
	tuCuenta: translate('general.nav.yourAccount'),
	cuentaYRol: translate('general.nav.accountAndRole'),
	mas: translate('general.nav.more')
});

/** El icono de cada sección, por su ruta (los mismos que tenían las barras de chirigota). */
const ICONO_DE_LA_RUTA: Record<string, React.FC<{ className?: string }>> = {
	home: Home,
	'my-benefit': Home,
	'my-benefit-individual': Home,
	staff: Users,
	'my-contributions': PiggyBank,
	'my-contributions-individual': PiggyBank,
	contributions: PiggyBank,
	'employment-pension-plan': FileText,
	'tu-plan': FileText,
	'set-benefit': FileText,
	simulators: Calculator,
	settings: Settings,
	billing: Receipt,
	'my-referrals': Gift,
	company: Building2
};

/** Las secciones de la barra lateral: todas las pestañas de la vista, en su orden. */
export const seccionesDeNavegacion = (pestanas: PestanaDeNavegacion[]): NavItemData[] =>
	pestanas.map(({ route, label }) => {
		const Icono = ICONO_DE_LA_RUTA[route] ?? Home;
		return { route, label, icon: <Icono className="h-6 w-6" /> };
	});

/** Cuántas secciones caben a la vista en la barra inferior; el resto va a «Más» (Proa). */
const A_LA_VISTA = 4;

/**
 * Las secciones de la barra inferior del móvil: cuatro a la vista y el resto en «Más».
 *
 * 🔴 Configuración se queda fuera: ya vive en la hoja de la cuenta. Con una excepción: si es la
 * pantalla en la que se está, sí sale. Se llega a ella desde el avatar, y sin esto la barra se
 * quedaba sin ninguna sección marcada.
 */
export const barraInferior = (
	pestanas: PestanaDeNavegacion[],
	rutaActiva: string
): { items: NavItemData[]; overflowItems: NavItemData[] } => {
	const secciones = seccionesDeNavegacion(
		pestanas.filter(({ route }) => route !== 'settings' || route === rutaActiva)
	);
	return { items: secciones.slice(0, A_LA_VISTA), overflowItems: secciones.slice(A_LA_VISTA) };
};
